import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { SupClass } from "../api/models/supClass.models";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { getClassLabelFromAkeneoObject } from "../utils/akeneoUtils";
import AddMaterialInputs from "./addMaterialInputs";
import { RowValuesWithEcomodClass } from "../api/models/rowValues.models";
import { Button } from "primereact/button";
import useReportRows from "../hooks/useReportRows";
import { CreateReportRowPayload } from "../api/models/reportRow.models";
import useReport from "../hooks/useReport";
import {
  CreateReportPayload,
  ReportStatus,
  ReportType,
} from "../api/models/report.models";
import { useShallow } from "zustand/shallow";
import { ReportingCycleEnum } from "../api/models/reportingCycle.models";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useReportingCycleContext } from "../contexts/reportingCycleContext";
import { useNavigate } from "react-router";
import GoBackLink from "./goBackLink";

const StyledView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  & form {
    width: 100%;

    & > div:first-child {
      padding: 0 2rem;
    }
  }

  .form-container {
    min-height: calc(100vh - 406px);
    width: 100%;
    max-width: 850px;
    text-align: left;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  margin-bottom: 1rem;
  color: var(--text-color);
  padding: 1rem 0rem;
  display: flex;
  align-items: center;
  padding: 0 2rem;
`;

const MaterialContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 850px;
  padding: 2rem;
`;

const SupRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const SupClassField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 0 1 500px;
  min-width: 200px;

  & label {
    font-size: 0.9rem;
    white-space: nowrap;
  }
`;

const StyledSupDropdown = styled(Dropdown)`
  flex: 1;
  min-width: 0;
  max-width: 500px;

  & .p-inputtext {
    font-size: 0.9rem;
  }
`;

const StickyActions = styled.div`
  display: flex;
  justify-content: flex-end;
  position: sticky;
  width: 100%;
  bottom: 0;
  background-color: #fff;
  z-index: 100;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
`;

interface CreateMaterialViewProps {
  reportId: number;
  onHide: () => void;
  ecomodClasses?: EcomodClass[];
  supClasses?: SupClass[];
  show?: boolean;
}

const CreateMaterialView: React.FC<CreateMaterialViewProps> = ({
  reportId,
  onHide,
  ecomodClasses,
  supClasses,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reportRowValues, setReportRowValues] = React.useState<
    RowValuesWithEcomodClass[]
  >([]);
  const [isSup, setIsSup] = React.useState(false);
  const [supClassCode, setSupClassCode] = React.useState<string | undefined>(
    undefined,
  );
  const [companyId, organizationId, year, quarter, month, toast, locale] =
    useUIStore(
      useShallow((state: UIState) => [
        state.companyId,
        state.organizationId,
        state.selectedYearForNewReport,
        state.selectedQuarterForNewReport,
        state.selectedMonthForNewReport,
        state.toastRef,
        state.locale,
      ]),
    );

  const supOptions = supClasses?.map((category) => ({
    label: getClassLabelFromAkeneoObject(locale, category),
    value: category.code,
  }));

  const { createReportRowData, isCreatingReportRow } = useReportRows();

  const { createNewReport, isCreatingNewReport } = useReport({
    reportId: undefined,
    disableSearch: true,
  });

  const { reportingCycle } = useReportingCycleContext();

  const handleCreateNewRow = async () => {
    // If no reportId is provided, create a new report first
    if (!reportId) {
      if (year === 0) {
        toast?.current?.show({
          severity: "error",
          detail: t("error-no-year-selected-detail"),
          life: 5000,
        });
        return;
      }

      const createReportPayload: CreateReportPayload = {
        companyId: companyId as number,
        organizationId: organizationId ? organizationId : undefined,
        year: year ? year : 2025,
        quarter:
          reportingCycle === ReportingCycleEnum.QUARTERLY
            ? quarter
              ? quarter
              : 1
            : undefined,
        month:
          reportingCycle === ReportingCycleEnum.MONTHLY
            ? month
              ? month
              : 1
            : undefined,
        reportStatus: ReportStatus.Draft,
        reportType: ReportType.EAN,
        rows: [],
      };
      const result = await createNewReport(createReportPayload, undefined);

      const payload: CreateReportRowPayload = {
        reportId: result.id,
        ean: "",
        name: reportRowValues[0]?.ecoModulationClass?.nameFi || "",
        active: true,
        amount: 1,
        values: reportRowValues.map((row) => ({
          ...row,
          isEmptyCup: row.isEmptyCup || false,
        })),
      };

      await createReportRowData(payload);
      if (result && result.id) {
        onHide();
        navigate(`/report/${result.id}`);
      }
    } else {
      const payload: CreateReportRowPayload = {
        reportId,
        ean: "",
        name: reportRowValues[0]?.ecoModulationClass?.nameFi || "",
        active: true,
        amount: 1,
        values: reportRowValues.map((row) => ({
          ...row,
          isEmptyCup: row.isEmptyCup || false,
        })),
      };

      await createReportRowData(payload);
      onHide();
    }
  };

  const handleSave = () => {
    handleCreateNewRow();
  };

  const saveDisabled = () => {
    return (
      reportRowValues?.length === 0 ||
      reportRowValues.some(
        (row) =>
          !row.ecoModulationClass ||
          row.value === null ||
          row.value === undefined ||
          isNaN(row.value) ||
          row.value < 0 ||
          reportRowValues.some(
            (value) =>
              !!value.secMaterial &&
              value.secValueType !== 2 &&
              (value.secValue ?? 0) > (value.value ?? 0),
          ),
      )
    );
  };

  return (
    <StyledView>
      <GoBackLink onClick={onHide}>
        {`${t("go-back")}` || "← Go back"}
      </GoBackLink>

      <br />

      <Description>{t("text-add-materials-description")}</Description>

      <MaterialContainer className="form-container">
        <SupRow>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "10px",
              marginRight: "20px",
            }}
          >
            <Checkbox
              inputId="isSup"
              checked={isSup}
              onChange={(e) => {
                setIsSup(!!e.checked);
                if (!e.checked) {
                  setSupClassCode(undefined);
                }
              }}
            />
            <label htmlFor="isSup" style={{ whiteSpace: "nowrap" }}>
              {t("label-single-use-plastic")}
            </label>
          </div>
          <SupClassField>
            <StyledSupDropdown
              disabled={!isSup}
              value={supClassCode}
              options={supOptions}
              onChange={(e) => setSupClassCode(e.value)}
              placeholder={t("label-select-sup-class")}
              showClear
            />
          </SupClassField>
        </SupRow>

        <AddMaterialInputs
          setReportRowValues={setReportRowValues}
          ecomodClasses={ecomodClasses}
          supClasses={supClasses}
          alignLeft={true}
          isMaterialCreation={true}
          isSup={isSup}
          supClass={supClassCode}
        />
      </MaterialContainer>
      <StickyActions>
        <Button
          role="cancel"
          type="button"
          label={t("cmd-cancel")}
          className="p-button-text"
          onClick={onHide}
          disabled={isCreatingReportRow || isCreatingNewReport}
        />
        <Button
          style={{ marginRight: "4rem" }}
          type="button"
          label={t("cmd-save")}
          className="p-ml-2"
          disabled={saveDisabled()}
          loading={isCreatingReportRow || isCreatingNewReport}
          onClick={handleSave}
        />
      </StickyActions>
    </StyledView>
  );
};

export default CreateMaterialView;
